import React, { useEffect, useState } from 'react';
import { Plus, Trash2, Zap, Send, RotateCw, Eye, EyeOff, Check, X, RefreshCw, ChevronDown, ChevronUp, Copy } from 'lucide-react';
import { webhooksApi } from '../../services/api';
import { useToast } from '../../context/ToastContext';

interface WebhookEvent {
  key: string;
  label: string;
  description: string;
}

interface Webhook {
  id: string;
  name: string;
  webhook_url: string;
  events: string[];
  is_active: boolean;
  secret_key: string | null;
  created_at: string;
  last_triggered_at: string | null;
  last_http_status: number | null;
}

interface WebhookLog {
  id: string;
  event_key: string;
  payload: any;
  response_status: number | null;
  success: boolean;
  sent_at: string;
  retry_count: number;
}

export default function WebhooksSettings() {
  const { showToast } = useToast();
  const [webhooks, setWebhooks] = useState<Webhook[]>([]);
  const [events, setEvents] = useState<WebhookEvent[]>([]);
  const [loading, setLoading] = useState(true);

  // Modal state
  const [showModal, setShowModal] = useState(false);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [formName, setFormName] = useState('');
  const [formUrl, setFormUrl] = useState('');
  const [formEvents, setFormEvents] = useState<string[]>([]);
  const [formSecret, setFormSecret] = useState('');
  const [saving, setSaving] = useState(false);
  const [formError, setFormError] = useState('');

  // Logs
  const [logsWebhookId, setLogsWebhookId] = useState<string | null>(null);
  const [logs, setLogs] = useState<WebhookLog[]>([]);
  const [logsLoading, setLogsLoading] = useState(false);

  // Test result
  const [testResult, setTestResult] = useState<{ id: string; success: boolean; status?: number } | null>(null);

  // Secret visibility
  const [showSecret, setShowSecret] = useState<Record<string, boolean>>({});

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    setLoading(true);
    try {
      const [whRes, evRes] = await Promise.all([webhooksApi.list(), webhooksApi.events()]);
      setWebhooks(whRes.data);
      setEvents(evRes.data);
    } catch {
    } finally {
      setLoading(false);
    }
  };

  const openCreateModal = () => {
    setEditingId(null);
    setFormName('');
    setFormUrl('');
    setFormEvents([]);
    setFormSecret('');
    setFormError('');
    setShowModal(true);
  };

  const openEditModal = (wh: Webhook) => {
    setEditingId(wh.id);
    setFormName(wh.name);
    setFormUrl(wh.webhook_url);
    setFormEvents([...wh.events]);
    setFormSecret('');
    setFormError('');
    setShowModal(true);
  };

  const handleSave = async () => {
    if (!formName.trim() || !formUrl.trim() || formEvents.length === 0) {
      setFormError('Name, URL und mindestens ein Event sind erforderlich.');
      return;
    }
    setSaving(true);
    setFormError('');
    try {
      if (editingId) {
        await webhooksApi.update(editingId, {
          name: formName,
          url: formUrl,
          events: formEvents,
          ...(formSecret ? { secret: formSecret } : {}),
        });
      } else {
        await webhooksApi.create({
          name: formName,
          url: formUrl,
          events: formEvents,
          ...(formSecret ? { secret: formSecret } : {}),
        });
      }
      setShowModal(false);
      loadData();
    } catch (err: any) {
      setFormError(err.response?.data?.detail || 'Fehler beim Speichern');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (id: string) => {
    if (!window.confirm('Webhook wirklich löschen?')) return;
    try {
      await webhooksApi.delete(id);
      loadData();
    } catch {
      showToast('Fehler beim Löschen', 'error');
    }
  };

  const handleToggle = async (id: string) => {
    try {
      await webhooksApi.toggle(id);
      loadData();
    } catch {
      showToast('Fehler beim Umschalten', 'error');
    }
  };

  const handleTest = async (id: string) => {
    setTestResult(null);
    try {
      const res = await webhooksApi.test(id);
      setTestResult({ id, success: res.data.success, status: res.data.status_code });
    } catch {
      setTestResult({ id, success: false });
    }
    setTimeout(() => setTestResult(null), 5000);
  };

  const loadLogs = async (webhookId: string) => {
    if (logsWebhookId === webhookId) {
      setLogsWebhookId(null);
      return;
    }
    setLogsWebhookId(webhookId);
    setLogsLoading(true);
    try {
      const res = await webhooksApi.logs(webhookId);
      setLogs(res.data);
    } catch {
      setLogs([]);
    } finally {
      setLogsLoading(false);
    }
  };

  const handleRetry = async (logId: string) => {
    try {
      await webhooksApi.retry(logId);
      if (logsWebhookId) loadLogs(logsWebhookId);
    } catch {
      showToast('Retry fehlgeschlagen', 'error');
    }
  };

  const toggleEventSelection = (key: string) => {
    setFormEvents(prev => prev.includes(key) ? prev.filter(e => e !== key) : [...prev, key]);
  };

  const getEventLabel = (key: string) => {
    const ev = events.find(e => e.key === key);
    return ev ? ev.label : key;
  };

  if (loading) {
    return <div className="card" style={{ padding: 32, textAlign: 'center' }}>Lade Webhooks...</div>;
  }

  return (
    <div style={{ maxWidth: 800 }}>
      <div className="card" style={{ padding: 24, marginBottom: 16 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 8 }}>
          <Zap size={18} style={{ color: 'var(--warning, #f59e0b)' }} />
          <strong>Webhooks & Automatisierung</strong>
        </div>
        <p className="text-muted text-sm" style={{ margin: 0 }}>
          Webhooks senden automatisch Daten an Zapier, Make oder eigene APIs, wenn bestimmte Ereignisse in CoachDesk365 eintreten.
          So kannst du z.B. automatisch E-Mails versenden, CRM-Systeme aktualisieren oder Benachrichtigungen auslösen.
        </p>
      </div>

      {/* Webhook list */}
      {webhooks.length === 0 ? (
        <div className="card" style={{ padding: 32, textAlign: 'center' }}>
          <Zap size={32} style={{ color: 'var(--border, #ccc)', marginBottom: 8 }} />
          <p className="text-muted">Noch keine Webhooks eingerichtet.</p>
          <button className="btn btn-primary" onClick={openCreateModal}>
            <Plus size={16} /> Webhook erstellen
          </button>
        </div>
      ) : (
        <>
          <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: 12 }}>
            <button className="btn btn-primary btn-sm" onClick={openCreateModal}>
              <Plus size={14} /> Neuer Webhook
            </button>
          </div>

          <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
            {webhooks.map(wh => (
              <div key={wh.id}>
                <div
                  className="card"
                  style={{
                    padding: '16px 20px',
                    opacity: wh.is_active ? 1 : 0.6,
                    borderLeft: `3px solid ${wh.is_active ? 'var(--success, #22c55e)' : 'var(--border, #ccc)'}`,
                  }}
                >
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 4 }}>
                        <strong style={{ fontSize: '0.95rem' }}>{wh.name}</strong>
                        <span
                          style={{
                            fontSize: '0.7rem',
                            padding: '1px 8px',
                            borderRadius: 10,
                            fontWeight: 600,
                            background: wh.is_active ? 'var(--success-bg, #dcfce7)' : 'var(--bg-muted, #f3f4f6)',
                            color: wh.is_active ? 'var(--success-text, #166534)' : 'var(--text-muted, #6b7280)',
                          }}
                        >
                          {wh.is_active ? 'Aktiv' : 'Inaktiv'}
                        </span>
                      </div>
                      <p className="text-muted text-sm" style={{ margin: '0 0 6px', wordBreak: 'break-all' }}>
                        {wh.webhook_url.length > 60 ? wh.webhook_url.slice(0, 60) + '...' : wh.webhook_url}
                      </p>
                      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4, marginBottom: 4 }}>
                        {wh.events.map(e => (
                          <span
                            key={e}
                            style={{
                              fontSize: '0.7rem',
                              padding: '2px 8px',
                              borderRadius: 10,
                              background: 'var(--info-bg, #f0f4ff)',
                              color: 'var(--info, #3b5998)',
                              fontWeight: 500,
                            }}
                          >
                            {getEventLabel(e)}
                          </span>
                        ))}
                      </div>
                      {wh.last_triggered_at && (
                        <p className="text-muted" style={{ fontSize: '0.7rem', margin: 0 }}>
                          Zuletzt: {new Date(wh.last_triggered_at).toLocaleString('de-DE')}
                          {wh.last_http_status && (
                            <span style={{ marginLeft: 6, color: wh.last_http_status < 300 ? 'var(--success, #16a34a)' : 'var(--danger, #dc2626)' }}>
                              HTTP {wh.last_http_status}
                            </span>
                          )}
                        </p>
                      )}
                    </div>

                    <div style={{ display: 'flex', gap: 4, marginLeft: 12, flexShrink: 0 }}>
                      <button className="btn btn-sm btn-secondary" title="Testen" onClick={() => handleTest(wh.id)}>
                        <Send size={13} />
                      </button>
                      <button className="btn btn-sm btn-secondary" title="Bearbeiten" onClick={() => openEditModal(wh)}>
                        <Zap size={13} />
                      </button>
                      <button
                        className="btn btn-sm btn-secondary"
                        title={wh.is_active ? 'Deaktivieren' : 'Aktivieren'}
                        onClick={() => handleToggle(wh.id)}
                      >
                        {wh.is_active ? <EyeOff size={13} /> : <Eye size={13} />}
                      </button>
                      <button className="btn btn-sm btn-secondary" title="Logs" onClick={() => loadLogs(wh.id)}>
                        {logsWebhookId === wh.id ? <ChevronUp size={13} /> : <ChevronDown size={13} />}
                      </button>
                      <button className="btn btn-sm btn-danger" title="Löschen" onClick={() => handleDelete(wh.id)}>
                        <Trash2 size={13} />
                      </button>
                    </div>
                  </div>

                  {testResult && testResult.id === wh.id && (
                    <div
                      style={{
                        marginTop: 8,
                        padding: '6px 12px',
                        borderRadius: 6,
                        fontSize: '0.8rem',
                        background: testResult.success ? 'var(--success-bg, #dcfce7)' : 'var(--danger-bg, #fef2f2)',
                        color: testResult.success ? 'var(--success-text, #166534)' : 'var(--danger-text, #991b1b)',
                      }}
                    >
                      {testResult.success ? (
                        <><Check size={13} style={{ verticalAlign: -2 }} /> Test erfolgreich (HTTP {testResult.status})</>
                      ) : (
                        <><X size={13} style={{ verticalAlign: -2 }} /> Test fehlgeschlagen{testResult.status ? ` (HTTP ${testResult.status})` : ''}</>
                      )}
                    </div>
                  )}
                </div>

                {/* Logs */}
                {logsWebhookId === wh.id && (
                  <div style={{ marginTop: 4, padding: '12px 16px', background: 'var(--bg)', borderRadius: 8, border: '1px solid var(--border-light, #e5e5e5)' }}>
                    <strong style={{ fontSize: '0.85rem' }}>Letzte Aufrufe</strong>
                    {logsLoading ? (
                      <p className="text-muted text-sm">Lade...</p>
                    ) : logs.length === 0 ? (
                      <p className="text-muted text-sm">Noch keine Log-Einträge.</p>
                    ) : (
                      <div style={{ marginTop: 8, display: 'flex', flexDirection: 'column', gap: 4 }}>
                        {logs.map(log => (
                          <div
                            key={log.id}
                            style={{
                              display: 'flex',
                              alignItems: 'center',
                              gap: 8,
                              padding: '6px 10px',
                              background: 'var(--bg-card)',
                              borderRadius: 6,
                              fontSize: '0.8rem',
                            }}
                          >
                            <span
                              style={{
                                width: 8,
                                height: 8,
                                borderRadius: '50%',
                                background: log.success ? 'var(--success, #22c55e)' : 'var(--danger, #ef4444)',
                                flexShrink: 0,
                              }}
                            />
                            <span style={{ fontWeight: 500, minWidth: 120 }}>{getEventLabel(log.event_key)}</span>
                            <span className="text-muted" style={{ fontSize: '0.75rem' }}>
                              {log.response_status ? `HTTP ${log.response_status}` : 'Fehler'}
                            </span>
                            <span className="text-muted" style={{ fontSize: '0.75rem', flex: 1 }}>
                              {new Date(log.sent_at).toLocaleString('de-DE')}
                            </span>
                            {log.retry_count > 0 && (
                              <span className="text-muted" style={{ fontSize: '0.7rem' }}>
                                {log.retry_count}x Retry
                              </span>
                            )}
                            {!log.success && (
                              <button
                                className="btn btn-sm"
                                style={{ padding: '2px 8px', fontSize: '0.7rem' }}
                                onClick={() => handleRetry(log.id)}
                                title="Erneut senden"
                              >
                                <RotateCw size={11} /> Retry
                              </button>
                            )}
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                )}
              </div>
            ))}
          </div>
        </>
      )}

      {/* Create/Edit Modal */}
      {showModal && (
        <div style={{
          position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.4)', display: 'flex',
          alignItems: 'center', justifyContent: 'center', zIndex: 1000,
        }}>
          <div className="card" style={{ padding: 28, width: 520, maxHeight: '80vh', overflow: 'auto' }}>
            <h3 style={{ marginTop: 0, marginBottom: 16 }}>
              {editingId ? 'Webhook bearbeiten' : 'Neuer Webhook'}
            </h3>

            {formError && (
              <div style={{ background: 'var(--danger-bg, #fef2f2)', color: 'var(--danger-text, #991b1b)', padding: '8px 12px', borderRadius: 6, marginBottom: 12, fontSize: '0.85rem' }}>
                {formError}
              </div>
            )}

            <div className="form-group">
              <label>Name</label>
              <input
                value={formName}
                onChange={e => setFormName(e.target.value)}
                placeholder="z.B. Zapier - Neuer Klient"
              />
            </div>

            <div className="form-group">
              <label>Webhook-URL</label>
              <input
                value={formUrl}
                onChange={e => setFormUrl(e.target.value)}
                placeholder="https://hooks.zapier.com/..."
              />
            </div>

            <div className="form-group">
              <label>Events</label>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 4, maxHeight: 200, overflow: 'auto', border: '1px solid var(--border, #e0ddd8)', borderRadius: 6, padding: 8 }}>
                {events.map(ev => (
                  <div
                    key={ev.key}
                    style={{
                      display: 'flex', alignItems: 'center', gap: 8, padding: '6px 8px',
                      borderRadius: 4, cursor: 'pointer',
                      background: formEvents.includes(ev.key) ? 'var(--info-bg, #eff6ff)' : 'transparent',
                    }}
                    onClick={() => toggleEventSelection(ev.key)}
                  >
                    <input
                      type="checkbox"
                      checked={formEvents.includes(ev.key)}
                      readOnly
                      style={{ width: 16, height: 16, cursor: 'pointer' }}
                    />
                    <div>
                      <span style={{ fontSize: '0.85rem', fontWeight: 500 }}>{ev.label}</span>
                      <p className="text-muted" style={{ fontSize: '0.75rem', margin: 0 }}>{ev.description}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            <div className="form-group">
              <label>Secret (optional, für HMAC-Signatur)</label>
              <input
                value={formSecret}
                onChange={e => setFormSecret(e.target.value)}
                placeholder={editingId ? 'Leer lassen = nicht ändern' : 'Wird automatisch generiert wenn leer'}
                type="password"
              />
            </div>

            <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end', marginTop: 16 }}>
              <button className="btn btn-secondary" onClick={() => setShowModal(false)}>Abbrechen</button>
              <button className="btn btn-primary" onClick={handleSave} disabled={saving}>
                {saving ? 'Speichern...' : 'Speichern'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
